import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, featuredVideos, latestUpdates } from '@/mocks/home';

interface VideoItem {
  id: number;
  title: string;
  category: string;
  categorySlug?: string;
  cover: string;
  description?: string;
  rating: number;
  duration?: string;
  episodes?: number;
  isHot: boolean;
  isNew: boolean;
  views: number;
  updateTime?: string;
  updateEpisode?: string;
}

const allVideos: VideoItem[] = [
  ...featuredVideos.map((v) => ({
    ...v,
    cover: v.cover,
  })),
  ...latestUpdates.map((v) => ({
    id: v.id,
    title: v.title,
    category: v.category,
    cover: v.cover,
    rating: 8.5,
    duration: v.updateEpisode === 'HD高清' ? '128分钟' : '45分钟/集',
    isHot: false,
    isNew: false,
    views: Math.floor(Math.random() * 5000000) + 1000000,
    description: '精彩内容，不容错过。高清画质，流畅播放，为您带来最佳的观影体验，让您沉浸在影视世界的魅力之中。',
    updateTime: v.updateTime,
  })),
];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = parseInt(id || '1', 10);
  const video = allVideos.find((v) => v.id === videoId) || allVideos[0];
  const cat = categories.find((c) => c.name === video.category || c.slug === video.categorySlug);

  const relatedVideos = allVideos
    .filter((v) => v.id !== video.id && (v.category === video.category || v.categorySlug === video.categorySlug))
    .slice(0, 6);

  const breadcrumbItems = [
    ...(cat ? [{ label: cat.name, href: `/category/${cat.slug}` }] : []),
    { label: video.title },
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        image: video.cover,
        datePublished: video.updateTime || '2026-08-01',
        author: { '@type': 'Organization', name: '玫瑰视频' },
        publisher: { '@type': 'Organization', name: '玫瑰视频' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://meiguishipin.com' },
          ...(cat ? [{ '@type': 'ListItem', position: 2, name: cat.name, item: `https://meiguishipin.com/category/${cat.slug}` }] : []),
          { '@type': 'ListItem', position: cat ? 3 : 2, name: video.title },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <main>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-6">
                  <img
                    src={video.cover}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-colors text-white cursor-pointer">
                      <i className="ri-play-fill text-2xl md:text-3xl"></i>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{video.title}</h1>
                  {video.isHot && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary-500 text-white whitespace-nowrap">热播中</span>
                  )}
                  {video.isNew && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-accent-500 text-white whitespace-nowrap">新上线</span>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-6 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    <strong className="text-foreground-900">{video.rating}</strong>
                  </span>
                  <span>{video.duration}</span>
                  {video.episodes && <span>共{video.episodes}集</span>}
                  <span className="whitespace-nowrap">{video.views.toLocaleString()}次播放</span>
                  {video.updateTime && <span className="whitespace-nowrap">更新于 {video.updateTime}</span>}
                  {cat && (
                    <Link
                      to={`/category/${cat.slug}`}
                      className="px-2.5 py-0.5 text-xs rounded-full bg-background-200 text-foreground-600 hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {video.category}
                    </Link>
                  )}
                </div>

                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h2 className="text-base font-semibold text-foreground-900 mb-3">内容简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                </div>

                <div className="bg-background-100 rounded-xl p-5">
                  <h2 className="text-base font-semibold text-foreground-900 mb-3">剧集列表</h2>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {Array.from({ length: video.episodes || 12 }, (_, i) => (
                      <button
                        key={i}
                        className={`px-2 py-2 text-xs rounded-lg transition-all duration-200 cursor-pointer ${
                          i === 0
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                        }`}
                      >
                        第{i + 1}集
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-background-100 rounded-xl p-5 sticky top-20">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">相关推荐</h3>
                  <div className="space-y-4">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-28 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={v.cover}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {v.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">{v.category}</p>
                          <span className="text-xs text-foreground-500 flex items-center gap-1 mt-1">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-star-fill text-accent-500 text-xs"></i>
                            </div>
                            {v.rating}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
        <Footer />
      </main>
    </>
  );
}